import React from "react";
import { Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { dt } from "../../../../shared/themes/redesignTokens";
import type { AccentTheme } from "../../../../shared/themes/accentThemes";

interface AccentColorPreviewCardProps {
  preset: AccentTheme;
  isActive: boolean;
  onClick: () => void;
  translate: (key: string) => string;
}

const AccentColorPreviewCard: React.FC<AccentColorPreviewCardProps> = ({
  preset,
  isActive,
  onClick,
  translate,
}) => {
  // Use the PRESET's colors inside the preview — NOT the active accent CSS vars
  const c = preset;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        border: `2px solid ${isActive ? dt.blue : dt.cardBorder}`,
        borderRadius: dt.radiusSm,
        padding: "16px",
        cursor: "pointer",
        backgroundColor: isActive ? dt.blueSoft : dt.cardBg,
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: isActive ? dt.blue : dt.textMuted,
          boxShadow: dt.cardShadowHover,
        },
      }}
    >
      {isActive && (
        <CheckCircleIcon
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: dt.blue,
            fontSize: 22,
          }}
        />
      )}

      {/* Preset name */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "14px",
          fontWeight: 700,
          color: dt.textPrimary,
          mb: 1.5,
        }}
      >
        {translate(preset.labelKey.replace("settings:", ""))}
      </Box>

      {/* Mini dashboard mock */}
      <Box
        sx={{
          backgroundColor: "#F8F9FA",
          border: "1px solid #E5E7EB",
          borderRadius: "6px",
          overflow: "hidden",
          minHeight: 120,
        }}
      >
        {/* Top accent bar */}
        <Box sx={{ height: 8, backgroundColor: c.primary }} />

        <Box sx={{ padding: "10px 12px" }}>
          {/* Nav pills row */}
          <Box sx={{ display: "flex", gap: "6px", mb: "10px" }}>
            {["Overview", "Machines", "Offers"].map((label, i) => (
              <Box
                key={label}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "10px",
                  fontWeight: i === 0 ? 600 : 400,
                  color: i === 0 ? "#FFFFFF" : dt.textSecondary,
                  backgroundColor: i === 0 ? c.primary : "#EBEDF0",
                  borderRadius: "10px",
                  px: "8px",
                  py: "3px",
                  lineHeight: 1.3,
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          {/* Sidebar snippet + stat area */}
          <Box sx={{ display: "flex", gap: "10px", mb: "10px" }}>
            {/* Sidebar items */}
            <Box sx={{ flex: "0 0 auto", display: "flex", flexDirection: "column", gap: "4px" }}>
              {["Dashboard", "Requests", "Settings"].map((label, i) => (
                <Box
                  key={label}
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "9px",
                    fontWeight: i === 0 ? 600 : 400,
                    color: i === 0 ? c.primary : dt.textMuted,
                    borderLeft: i === 0 ? `2px solid ${c.primary}` : "2px solid transparent",
                    pl: "6px",
                    py: "2px",
                    lineHeight: 1.3,
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>

            {/* Stat value */}
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Box
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "20px",
                  fontWeight: 700,
                  color: c.primary,
                  lineHeight: 1.2,
                }}
              >
                94.2%
              </Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "9px",
                  color: dt.textSecondary,
                  mt: "2px",
                }}
              >
                Uptime
              </Box>
            </Box>
          </Box>

          {/* Action button */}
          <Box
            sx={{
              display: "inline-block",
              fontFamily: dt.font,
              fontSize: "10px",
              fontWeight: 600,
              color: "#FFFFFF",
              backgroundColor: c.primary,
              borderRadius: "4px",
              px: "10px",
              py: "4px",
              lineHeight: 1.3,
              mb: "10px",
            }}
          >
            View Details
          </Box>

          {/* Swatch bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              pt: "8px",
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "3px",
                backgroundColor: c.primary,
              }}
            />
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "3px",
                backgroundColor: c.glow,
                border: `1px solid ${c.primary}`,
              }}
            />
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "3px",
                backgroundColor: c.soft,
                border: "1px solid #E5E7EB",
              }}
            />
            <Box
              sx={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                fontSize: "9px",
                color: dt.textMuted,
                ml: "2px",
              }}
            >
              {c.primary}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AccentColorPreviewCard;
